import { NavigateFunction } from 'react-router-dom'

/**
 * Navigation utility functions for tray management
 * Follows clean code principles with clear separation of concerns
 */

export interface NavigationSource {
  fromSurgery?: string
  surgeryId?: string
  fromSurgeries?: boolean
  selectedSurgeryId?: string
  beforeDate?: string
  afterDate?: string
}

/**
 * Navigate to tray details with source tracking
 * @param navigate - React Router navigate function
 * @param trayBarcode - The tray barcode to navigate to
 * @param source - Optional source information for back navigation
 */
export const navigateToTrayDetails = (
  navigate: NavigateFunction,
  trayBarcode: string,
  source?: NavigationSource
): void => {
  const searchParams = new URLSearchParams()

  if (source?.fromSurgery && source?.surgeryId) {
    searchParams.set('fromSurgery', source.fromSurgery)
    searchParams.set('surgeryId', source.surgeryId)
  }
  if (source?.fromSurgeries) {
    searchParams.set('fromSurgeries', 'true')
  }
  if (source?.selectedSurgeryId) {
    searchParams.set('selectedSurgeryId', source.selectedSurgeryId)
  }
  if (source?.beforeDate) {
    searchParams.set('beforeDate', source.beforeDate)
  }
  if (source?.afterDate) {
    searchParams.set('afterDate', source.afterDate)
  }

  const queryString = searchParams.toString()
  const url = `/tray-management/${trayBarcode}${
    queryString ? `?${queryString}` : ''
  }`

  navigate(url)
}

/**
 * Get navigation source from URL search parameters
 * @param searchParams - URL search parameters
 * @returns Navigation source information
 */
export const getNavigationSource = (
  searchParams: URLSearchParams
): NavigationSource => {
  return {
    fromSurgery: searchParams.get('fromSurgery') || undefined,
    surgeryId: searchParams.get('surgeryId') || undefined,
    fromSurgeries: searchParams.get('fromSurgeries') === 'true' ? true : undefined,
    selectedSurgeryId: searchParams.get('selectedSurgeryId') || undefined,
    beforeDate: searchParams.get('beforeDate') || undefined,
    afterDate: searchParams.get('afterDate') || undefined,
  }
}

/**
 * Navigate back based on source information
 * @param navigate - React Router navigate function
 * @param source - Navigation source information
 */
export const navigateBack = (
  navigate: NavigateFunction,
  source?: NavigationSource
): void => {
  if (source?.fromSurgeries) {
    // Navigate back to surgeries route with filters and selectedSurgeryId
    const params = new URLSearchParams()
    if (source.selectedSurgeryId) params.set('selectedSurgeryId', source.selectedSurgeryId)
    if (source.beforeDate) params.set('beforeDate', source.beforeDate)
    if (source.afterDate) params.set('afterDate', source.afterDate)
    navigate(`/surgeries${params.toString() ? `?${params.toString()}` : ''}`)
    return
  }
  if (source?.fromSurgery && source?.surgeryId) {
    // Navigate back to surgery route
    navigate(`/surgeries/${source.surgeryId}`)
  } else {
    // Default back navigation
    navigate('..')
  }
}
